/* eslint-disable react/jsx-no-useless-fragment */
import React, { useEffect, useMemo, useState } from 'react';
import Cookies from 'universal-cookie/cjs';
import axios from 'axios';
import { Spin, Modal } from 'antd';
import { Col, Input, Label, Row } from 'reactstrap';
import Select from 'react-select';
import moment from 'moment';
import { debounce } from 'lodash';
import { CreateRecordButtons, UpdateRecordButtons } from '../../../components/common/profile';
import { deleteRecord, updateRecord, createRecord } from '../../../util/formHelper';
import { waitingListDefaultObj, statusOptions } from './constants';
import ErrorMessage from './errorMessage';

const cookies = new Cookies();
const { REACT_APP_API_DOMAIN } = process.env;

const requiredFields = ['status', 'priority', 'CustomerId', 'CarId', 'ParkingPlanId', 'CarParkId'];

function WaitingForm({ fetch, update, type, cancel, allowDelete, recordData }) {
  const [isLoading, setIsLoading] = useState(false);
  const [waitingList, setWaitingList] = useState(waitingListDefaultObj);
  const [showModal, setShowModal] = useState(false);

  const [carParks, setCarParks] = useState([]);
  const [parkingPlans, setParkingPlans] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cars, setCars] = useState([]);
  const [isCustomerLoading, setIsCustomerLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const currentParkingPlan = useMemo(
    () => parkingPlans.find((p) => p.value === waitingList.ParkingPlanId) || {},
    [parkingPlans, waitingList.ParkingPlanId]
  );

  async function fetchCars(customerId, Car) {
    try {
      const {
        data: { data },
      } = await axios.get(`${REACT_APP_API_DOMAIN}/car?customerId=${customerId}`, {
        headers: { token: cookies.get('token') },
      });

      const options = data.map((e) => ({
        value: e.id,
        label: e.registrationMark,
        CarTypeId: e.CarTypeId,
      }));

      if (Car?.id) {
        const isExistingCar = options.some((e) => e.id === Car.id);
        if (!isExistingCar) {
          options.push({
            value: Car.id,
            label: Car.registrationMark,
            CarTypeId: Car.CarTypeId,
          });
        }
      }

      setCars(options);
      return Promise.resolve(options);
    } catch (error) {
      if (Car?.id) {
        setWaitingList((prev) => ({ ...prev, CustomerId: customerId, CarId: Car.id }));
        setCars([{ value: Car.id, label: Car.registrationMark, CarTypeId: Car.CarTypeId }]);
      }

      return Promise.resolve([]);
    }
  }

  async function fetchCustomers(phone = '') {
    try {
      setIsCustomerLoading(true);
      const {
        data: { data },
      } = await axios.get(
        phone
          ? `${REACT_APP_API_DOMAIN}/customer?phone=${phone}`
          : `${REACT_APP_API_DOMAIN}/customer`,
        {
          headers: { token: cookies.get('token') },
        }
      );

      const options = data.map((e) => ({
        value: e.id,
        label: `${e.firstName} ${e.lastName} ${e.phone ? `(${e.phone})` : ''}`,
        phone: e.phone,
      }));

      setCustomers(options);
      return Promise.resolve(options);
    } catch (error) {
      setCustomers([]);
      return Promise.resolve([]);
    } finally {
      setIsCustomerLoading(false);
    }
  }

  async function fetchCarParks() {
    try {
      const {
        data: { data },
      } = await axios.get(`${REACT_APP_API_DOMAIN}/car-park`, {
        headers: { token: cookies.get('token') },
      });

      const carparkData = data.map((e) => ({ value: e.id, label: `(${e.code}) ${e.name}` }));

      setCarParks(carparkData);
      return Promise.resolve(carparkData);
    } catch (error) {
      return Promise.resolve(error);
    }
  }

  async function fetchParkingPlans(carparkId) {
    try {
      const {
        data: { data },
      } = await axios.get(`${REACT_APP_API_DOMAIN}/parkingplan?CarParkId=${carparkId}`, {
        headers: { token: cookies.get('token') },
      });

      const options = data
        .filter((e) => e.isFull && e.isWaitingList)
        .map(({ id, subTitle, CarTypeId, CarType = {} }) => ({
          value: id,
          label: subTitle ? `${CarType.name} | ${subTitle}` : 'N/A',
          CarTypeId,
        }));

      setParkingPlans(options);
      return Promise.resolve(options);
    } catch (error) {
      setParkingPlans([]);
      return Promise.reject(error);
    }
  }

  async function fetchWaitingList() {
    try {
      const {
        data: { data },
      } = await axios.get(fetch, { headers: { token: cookies.get('token') } });
      setWaitingList(data);
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  const fetchData = async () => {
    try {
      setIsLoading(true);

      if (type === 'edit') {
        const {
          CarPark = {},
          Car = {},
          Customer = {},
          ParkingPlan = {},
        } = await fetchWaitingList();

        await fetchCars(Customer.id, Car);

        setParkingPlans([
          {
            value: ParkingPlan.id,
            label: ParkingPlan.subTitle || 'N/A',
            CarTypeId: ParkingPlan.CarTypeId,
          },
        ]);
        setCustomers([{ value: Customer.id, label: `${Customer.firstName} ${Customer.lastName}` }]);
        setCarParks([{ value: CarPark.id, label: `(${CarPark.code}) ${CarPark.name}` }]);
        setIsLoading(false);
        return;
      }

      await fetchCarParks();
      await fetchCustomers();

      setWaitingList(waitingListDefaultObj);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  function onCustomInputChange(phone = '', action = '') {
    if (
      (action.action === 'menu-close' && !phone) ||
      (phone && phone.length === 8) ||
      (action.action === 'input-change' && !phone)
    ) {
      fetchCustomers(phone);
    }
  }

  const debouncedOnCustomInputChange = debounce(onCustomInputChange, 500);

  const handleSave = async () => {
    const { CarId, ParkingPlanId, status, remarks, CustomerId, priority, manualInputDate } =
      waitingList || {};

    const missingFields = requiredFields.filter((e) => !waitingList[e]);

    if (missingFields.length > 0) {
      setErrors(
        requiredFields.reduce((acc, v) => {
          acc[v] = missingFields.includes(v) ? 'request field' : undefined;
          return acc;
        }, {})
      );
      alert('Something went wrong. Please make sure to fill in all the information');
      return;
    }

    const body = {
      CarId,
      ParkingPlanId,
      status,
      remarks: remarks || undefined,
      CustomerId,
      priority: priority || undefined,
      manualInputDate: manualInputDate || undefined,
    };

    if (type === 'edit') {
      updateRecord(update, body, true);
    } else {
      createRecord(`${process.env.REACT_APP_API_DOMAIN}/WaitingList`, body);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  function renderConfirmDeleteModal(e) {
    return (
      <Modal
        title="Delete record?"
        visible={showModal}
        onOk={() => deleteRecord(e)}
        okText="Confirm"
        onCancel={() => setShowModal(false)}
        centered
      >
        Please click "Confirm" to delete the record.
      </Modal>
    );
  }

  return (
    <>
      {isLoading ? (
        <div className="d-flex justify-content-center">
          <Spin />
        </div>
      ) : (
        <>
          <Row>
            {type === 'edit' && (
              <Col md={6} className="mb-4">
                <Label>id</Label>
                <Input disabled type="text" value={waitingList.id} name="username" />
              </Col>
            )}

            <Col md={6} className="mb-4">
              <Label>
                Status
                <span style={{ color: 'red' }}>*</span>
              </Label>
              <Select
                menuPlacement="auto"
                menuPosition="fixed"
                options={statusOptions}
                value={statusOptions.find((e) => e.value === waitingList.status)}
                onChange={(selected) =>
                  setWaitingList({
                    ...waitingList,
                    status: selected.value,
                  })
                }
                isOptionDisabled={(e) => e.isDisable}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderColor: errors.status !== undefined ? 'red' : 'hsl(0,0%,80%)',
                  }),
                }}
              />
              <ErrorMessage name="status" errors={errors} />
            </Col>

            {type === 'edit' && (
              <Col md={6} className="mb-4">
                <Label>reference</Label>
                <Input disabled type="text" value={waitingList.reference} name="reference" />
              </Col>
            )}

            <Col md={6} className="mb-4">
              <Label>
                Priority
                <span style={{ color: 'red' }}>*</span>
              </Label>
              <Input
                type="number"
                value={waitingList.priority}
                onChange={(e) => {
                  setWaitingList({
                    ...waitingList,
                    priority: e.target.value ? Number(e.target.value) : '',
                  });
                }}
                name="priority"
                style={{ borderColor: errors.status !== undefined ? 'red' : '#ced4da' }}
              />
              <ErrorMessage name="priority" errors={errors} />
            </Col>
          </Row>

          <Row>
            <Col md={6} className="mb-4">
              <Label>
                CarPark
                <span style={{ color: 'red' }}>*</span>
              </Label>
              <Select
                menuPlacement="auto"
                menuPosition="fixed"
                options={carParks}
                value={carParks.find((e) => e.value === waitingList.CarParkId)}
                onChange={(selected) => {
                  setWaitingList({
                    ...waitingList,
                    CarParkId: selected.value,
                    ParkingPlanId: null,
                    CarId: null,
                  });
                  fetchParkingPlans(selected.value);
                }}
                isDisabled={type === 'edit'}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderColor: errors.CarParkId !== undefined ? 'red' : 'hsl(0,0%,80%)',
                  }),
                }}
              />
              <ErrorMessage name="CarParkId" errors={errors} />
            </Col>

            <Col md={6} className="mb-4">
              <Label>
                Parking Plan
                <span style={{ color: 'red' }}>*</span>
              </Label>
              <Select
                menuPlacement="auto"
                menuPosition="fixed"
                options={parkingPlans}
                value={parkingPlans.find((e) => e.value === waitingList.ParkingPlanId) || null}
                onChange={(selected) =>
                  setWaitingList({
                    ...waitingList,
                    ParkingPlanId: selected.value,
                    CarId: null,
                  })
                }
                isDisabled={type === 'edit' || !waitingList.CarParkId}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderColor: errors.ParkingPlanId !== undefined ? 'red' : 'hsl(0,0%,80%)',
                  }),
                }}
              />
              <ErrorMessage name="ParkingPlanId" errors={errors} />
            </Col>

            <Col md={6} className="mb-4">
              <Label>
                Customer
                <span style={{ color: 'red' }}>*</span>
              </Label>
              <Select
                menuPlacement="auto"
                menuPosition="fixed"
                options={customers}
                value={customers.find((e) => e.value === waitingList.CustomerId)}
                onChange={(selected) => {
                  setWaitingList({
                    ...waitingList,
                    CustomerId: selected.value,
                    CarId: null,
                  });
                  fetchCars(selected.value, waitingList.Car);
                }}
                isDisabled={type === 'edit'}
                onInputChange={debouncedOnCustomInputChange}
                isLoading={isCustomerLoading}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderColor: errors.CustomerId !== undefined ? 'red' : 'hsl(0,0%,80%)',
                  }),
                }}
              />
              <ErrorMessage name="CustomerId" errors={errors} />
            </Col>

            <Col md={6} className="mb-4">
              <Label>
                Cars
                <span style={{ color: 'red' }}>*</span>
              </Label>
              <Select
                menuPlacement="auto"
                menuPosition="fixed"
                options={cars.filter((e) => e.CarTypeId === currentParkingPlan.CarTypeId)}
                value={cars.find((e) => e.value === waitingList.CarId) || null}
                onChange={(selected) =>
                  setWaitingList({
                    ...waitingList,
                    CarId: selected.value,
                  })
                }
                isDisabled={
                  !waitingList.CustomerId ||
                  !waitingList.ParkingPlanId ||
                  (waitingList.status !== 'confirmed' && waitingList.status !== 'processed')
                }
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderColor: errors.CarId !== undefined ? 'red' : 'hsl(0,0%,80%)',
                  }),
                }}
              />
              <ErrorMessage name="CarId" errors={errors} />
            </Col>

            {type === 'edit' && (
              <Col md={6} className="mb-4">
                <Label>Created At</Label>
                <Input
                  disabled
                  type="text"
                  value={moment(waitingList.createdAt).format('MM/DD/YYYY')}
                  name="createdAt"
                />
              </Col>
            )}

            <Col md={6} className="mb-4">
              <div className="d-flex flex-column">
                <Label>Manual Input Date</Label>
                <Input
                  type="text"
                  value={waitingList.manualInputDate}
                  onChange={(e) => {
                    setWaitingList({
                      ...waitingList,
                      manualInputDate: e.target.value,
                    });
                  }}
                  name="manualInputDate"
                />
                {/* <DatePicker
                  value={waitingList.manualInputDate ? moment(waitingList.manualInputDate) : null}
                  className="mb-3"
                  onChange={(date) => {
                    setWaitingList({
                      ...waitingList,
                      manualInputDate: date,
                    });
                  }}
                  format="YYYY-MM-DD HH:mm"
                /> */}
              </div>
            </Col>

            <Col md={6} className="mb-4">
              <Label>Remarks</Label>
              <Input
                type="textarea"
                value={waitingList.remarks}
                onChange={(e) => {
                  setWaitingList({
                    ...waitingList,
                    remarks: e.target.value,
                  });
                }}
                name="remarks"
                style={{ height: '100px' }}
              />
            </Col>
          </Row>

          {type === 'edit' ? (
            <>
              {renderConfirmDeleteModal(update)}
              <UpdateRecordButtons
                handleSave={handleSave}
                handleDelete={() => setShowModal(true)}
                cancel={cancel}
                allowDelete={allowDelete}
              />
            </>
          ) : (
            <CreateRecordButtons handleSave={handleSave} cancel={cancel} />
          )}
        </>
      )}
    </>
  );
}

export default WaitingForm;
